import type { BreakdownReference } from '@/types/ai-session';
import {
  GENDER_LABELS,
  RACE_LABELS,
  VISUAL_AGE_LABELS,
  getGenderLabel,
  getAssetMetadataLabel,
  getLocationLabel,
  getPropTypeLabel,
  getRaceLabel,
  getSeasonLabel,
  getTimeOfDayLabel,
  getVisualAgeLabel,
  getWeatherLabel,
} from '@/constants/preview-enums';
import { getCameraMovementLabel, getShotSizeLabel } from '@/constants/storyboard';
import type { CanonicalAISession } from '@/lib/ai-session/contracts/types';
import { formatShotSplitCommand } from '@/lib/ai-session/presentation/composer/mentions';
import type { ActiveAISessionEditTarget, AISessionChatPanelPlacement } from '@/stores/uiStore';

export const SESSION_TYPE_SCREENPLAY_ASSET_BREAKDOWN = 3;
export const SESSION_TYPE_SCREENPLAY_STORYBOARD_BREAKDOWN = 4;

export function assetVisibleName(item: {
  name?: string | null;
  metaData?: Record<string, unknown> | null;
}): string {
  const stored = typeof item.name === 'string' ? item.name.trim() : '';
  const baseValue = item.metaData?.checklistBaseName;
  const base = typeof baseValue === 'string' ? baseValue.trim() : '';
  // 只在存储名仍是基础名，或仍是「基础名 · 归属」这类历史消歧名时，才用基础名展示。
  if (base && (stored === base || stored.startsWith(`${base} ·`))) return base;
  return stored;
}

export type AssetItem = {
  schemaVersion: number;
  draftItemId: string;
  kind: string;
  name: string;
  description?: string;
  metaData: Record<string, unknown>;
  roleType?: string;
  isOffscreen?: boolean;
  gender?: string;
  race?: string;
  visualAge?: string;
  location?: string;
  realWorldLocation?: string;
  propType?: string;
  ownerCharacter?: string;
  ownerLocation?: string;
  usage?: string;
  defaultVariantDraftId?: string;
  variants: AssetVariant[];
};

export type AssetVariant = {
  variantDraftId: string;
  tagLabel: string;
  isDefault?: boolean;
  description?: string;
  visualDescription?: string;
  clothing?: string;
  hair?: string;
  makeup?: string;
  accessories?: string[];
  applicableScenes?: string[];
  timeOfDay?: string;
  weather?: string;
  season?: string;
  atmosphere?: string;
  usage?: string;
  metaData: Record<string, unknown>;
};

export type AssetResult = {
  characters: AssetItem[];
  locations: AssetItem[];
  props: AssetItem[];
};

export type AssetGroupKey = keyof AssetResult;
export type AssetConfirmationStep = 'names' | 'labels';
export type AssetConfirmationState = Record<AssetGroupKey, Record<AssetConfirmationStep, boolean>>;

export type CharacterBasicEditForm = {
  originalName: string;
  name: string;
  roleType: string;
  race: string;
  gender: string;
  visualAge: string;
  isOffscreen: boolean;
  description: string;
};

export const ASSET_GROUPS: Array<{ key: AssetGroupKey; label: string }> = [
  { key: 'characters', label: '角色' },
  { key: 'locations', label: '场景' },
  { key: 'props', label: '道具' },
];

export const ASSET_CONFIRMATION_STEPS: Array<{
  key: AssetConfirmationStep;
  label: string;
}> = [
  { key: 'names', label: '数量和名称' },
  { key: 'labels', label: '标签数量和名称' },
];

export const AI_EDIT_PANEL_PLACEMENT: AISessionChatPanelPlacement = {
  preset: 'left-of-storyboard-panel',
  width: 600,
  height: 640,
  offset: 8,
};

export const ASSET_GROUP_MENTION_LABEL: Record<AssetGroupKey, string> = {
  characters: '角色',
  locations: '场景',
  props: '道具',
};

export function getLocationTypeLabel(value?: string | null): string | undefined {
  return getLocationLabel(value);
}

export type AssetSplitKind = 'outfits' | 'sets' | 'states';
export type AssetSplitEntry = {
  variantDraftId?: string;
  name: string;
  description?: string;
  visualDescription?: string;
};

export const ASSET_SPLIT_CONFIG: Record<
  AssetGroupKey,
  { kind: AssetSplitKind; menuLabel: string; sectionLabel: string; unitLabel: string }
> = {
  characters: {
    kind: 'outfits',
    menuLabel: '拆分装扮',
    sectionLabel: '装扮拆分',
    unitLabel: '套装扮',
  },
  locations: { kind: 'sets', menuLabel: '细化布局', sectionLabel: '布景拆分', unitLabel: '处布景' },
  props: { kind: 'states', menuLabel: '细化状态', sectionLabel: '状态拆分', unitLabel: '个状态' },
};

export function buildAssetEditAssistantHint(
  group: AssetGroupKey,
  item: AssetItem,
  detailOnly = false
): string {
  const mentionLabel = ASSET_GROUP_MENTION_LABEL[group];
  const fieldHints =
    group === 'characters'
      ? '角色基本信息、角色简介、装扮信息或主标签'
      : group === 'locations'
        ? '场景基本信息、空间描述、时间/天气/季节或主标签'
        : '道具基本信息、用途、归属关系或材质/颜色标签';
  if (detailOnly) {
    return `可补充${mentionLabel}「${item.name}」的详细描述（如外观、氛围、材质）；如需新增、删除或改名，请在工作区操作，清单将自动解冻并需重新确认。`;
  }
  return `直接描述要调整的${fieldHints}。`;
}

export const ASSET_SUPPLEMENT_FIELD_EXAMPLE = '扩展字段：名称=内容';

const SUPPLEMENT_EXAMPLE_SKIP_LABELS = new Set([
  '角色名称',
  '场景名称',
  '道具名称',
  '类型',
  '角色简介',
  '场景描述',
  '道具视觉说明',
  '记忆点',
  '性格描述',
]);

function isUnfilledSupplementValue(value: string): boolean {
  return /未填写|未明确|未知|待确认|待补充|^-$/u.test(value.trim());
}

function supplementExampleLabel(label: string): string {
  if (label === '使用方法/关键功能') return '用途';
  if (label === '昼夜时间') return '时间';
  return label;
}

export function buildAssetSupplementFieldExample(group: AssetGroupKey, item: AssetItem): string {
  const rows =
    group === 'characters'
      ? [...getCharacterBasicRows(item), ...getCharacterOutfit(item).rows]
      : group === 'locations'
        ? [...getLocationBasicRows(item), ...getLocationMainSet(item).rows]
        : [...getPropBasicRows(item), ...getPropMainState(item).rows];
  const openField = rows.find(
    (row) => !SUPPLEMENT_EXAMPLE_SKIP_LABELS.has(row.label) && isUnfilledSupplementValue(row.value)
  );
  if (!openField) return ASSET_SUPPLEMENT_FIELD_EXAMPLE;
  return `扩展字段：${supplementExampleLabel(openField.label)}=内容`;
}

const LOCATION_STRUCTURED_FIELD_CODES = [
  ['时间', 'timeOfDay', { 夜晚: 'night', 白天: 'day', 黎明: 'dawn', 黄昏: 'dusk' }],
  [
    '天气',
    'weather',
    { 雨天: 'rainy', 雪天: 'snowy', 雾天: 'foggy', 多云: 'cloudy', 晴天: 'sunny', 大风: 'windy' },
  ],
  ['内外景', 'location', { 内景: 'interior', 外景: 'exterior' }],
] as const;

function locationStructuredFieldAssignment(assignment: string): string | null {
  const valuesByLabel = new Map<string, Set<string>>();
  for (const match of assignment.matchAll(/(时间|天气|内外景)=([^，,；;]+)/g)) {
    const label = match[1] ?? '';
    const value = (match[2] ?? '').trim();
    const values = valuesByLabel.get(label) ?? new Set<string>();
    values.add(value);
    valuesByLabel.set(label, values);
  }
  if (valuesByLabel.size === 0) return null;
  const parts: string[] = [];
  for (const [label, field, codes] of LOCATION_STRUCTURED_FIELD_CODES) {
    const values = valuesByLabel.get(label);
    if (!values) continue;
    if (values.size !== 1) return null;
    const value = [...values][0] ?? '';
    const code = codes[value as keyof typeof codes];
    if (!code) return null;
    parts.push(`${field}=${code}`);
  }
  if (parts.length !== valuesByLabel.size) return null;
  return parts.join('，');
}

export function buildAssetSupplementFieldPrompt(
  group: AssetGroupKey,
  item: AssetItem,
  assignment?: string
): string {
  const mention = getAssetMentionToken(group, item.name);
  const body = assignment?.trim();
  if (!body) return `${mention} ${buildAssetSupplementFieldExample(group, item)}`;
  if (group === 'locations') {
    const structured = locationStructuredFieldAssignment(body);
    const assignmentText = structured ?? body;
    return `${mention} 请更新已有字段：${assignmentText}。不要写入补充信息。`;
  }
  return `${mention} 补充字段：${body}`;
}

export function buildAssetSupplementFieldAssistantHint(
  _group: AssetGroupKey,
  _item: AssetItem,
  _applied?: { assignment: string; sceneLabels: string[] }
): string {
  return '';
}

export function retainSceneSupplementHint(hint: string, _composerText: string): string {
  const trimmed = hint.trim();
  if (!trimmed) return '';
  if (
    trimmed.includes('已填入') ||
    trimmed.startsWith('场次条件不一致') ||
    trimmed.includes('没有写明可补充') ||
    trimmed.includes('换成要补的字段')
  ) {
    return '';
  }
  return trimmed;
}

export function buildAssetSplitPrompt(group: AssetGroupKey, item: AssetItem): string {
  const mentionToken = getAssetMentionToken(group, item.name);
  if (group === 'characters') {
    return `${mentionToken} 请基于当前资产草稿和相关出现场次，拆分该角色的装扮；仅根据剧情中实际存在的换装需求，拆分为日常/外出/特殊场景等多套，并为每套补充简短名称、视觉描述、关键服饰细节和适用场景。`;
  }
  if (group === 'locations') {
    return `${mentionToken} 请基于当前资产草稿和相关出现场次，细化该场景的空间布局；如有必要，可拆分为主景/局部/不同时间状态等多处布景，并为每处补充简短名称、布局描述、关键陈设和氛围。`;
  }
  return `${mentionToken} 请基于当前资产草稿和相关出现场次，细化该道具的状态信息；如剧情中状态会变化，可拆分为完好/损坏/使用中等多个状态，并为每个状态补充简短名称、外观变化、使用方式和剧情作用。`;
}

export function getAssetSplitEntries(item: AssetItem, kind: AssetSplitKind): AssetSplitEntry[] {
  if (item.variants.length > 1) {
    return item.variants.map((variant) => ({
      variantDraftId: variant.variantDraftId,
      name: variant.tagLabel,
      description:
        variant.description ??
        getStringValue(variant.metaData, [
          'description',
          'desc',
          'clothing',
          'props',
          'usage',
          'atmosphere',
        ]),
      visualDescription:
        variant.visualDescription ??
        getStringValue(variant.metaData, [
          'visualDescription',
          'appearance',
          'setDescription',
          'stateDescription',
        ]),
    }));
  }

  const raw = item.metaData[kind];
  if (!Array.isArray(raw)) return [];
  return raw.reduce<AssetSplitEntry[]>((entries, value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return entries;
    const record = value as Record<string, unknown>;
    const name = typeof record.name === 'string' ? record.name.trim() : '';
    if (!name) return entries;
    entries.push({
      name,
      description: getStringValue(record, ['description', 'desc']),
      visualDescription: getStringValue(record, ['visualDescription']),
    });
    return entries;
  }, []);
}

export function getAssetMentionToken(group: AssetGroupKey, name: string): string {
  return `@[${ASSET_GROUP_MENTION_LABEL[group]}:${name}]`;
}

export function buildStoryboardEditAssistantHint(shotNumber: number): string {
  return `已定位到镜号 ${shotNumber}。准备默认重写要求，可修改后发送；已有正文时请选择追加、替换或取消。`;
}

export function buildStoryboardSplitComposerText(shotNumber: number): string {
  return `@[镜号:${shotNumber}] ${formatShotSplitCommand(2)} `;
}

/** 资产卡片、分镜卡片上的「补充字段」按钮暂时隐藏；指令组装与读取字段的调用链保留。 */
export const SUPPLEMENT_FIELD_CARD_ENTRY_VISIBLE: boolean = false;

export const STORYBOARD_SUPPLEMENT_FIELD_EXAMPLE = '扩展字段：画面贴片=码头夜雨';

export function buildStoryboardSupplementFieldComposerText(
  shotNumber: number,
  assignment?: string
): string {
  const body = assignment?.trim();
  if (body) return `@[镜号:${shotNumber}] 补充字段：${body}`;
  return `@[镜号:${shotNumber}] ${STORYBOARD_SUPPLEMENT_FIELD_EXAMPLE}`;
}

export function buildStoryboardSupplementFieldAssistantHint(_shotNumber: number): string {
  return '';
}

export function buildStoryboardSplitAssistantHint(shotNumber: number): string {
  return `已定位到镜号 ${shotNumber}。默认拆成 2 镜，可改成 3 到 5 镜，也可以再补一句切法。`;
}

export function getEditTargetStatusLabel(
  target: Pick<ActiveAISessionEditTarget, 'status' | 'quiet'> | null
): string | null {
  if (!target) return null;
  if (target.status === 'running') return '正在修改';
  if (target.status === 'selected' && target.quiet) return null;
  if (target.status === 'selected') return '待修改';
  if (target.status === 'completed') return '已修改';
  if (target.status === 'failed') return '修改失败';
  return null;
}

const ROLE_TYPE_LABELS: Record<string, string> = {
  main: '主角',
  supporting: '配角',
  extra: '群演',
};

const ASSET_VALUE_LABELS: Record<string, string> = {
  unknown: '未知',
  paper: '纸张',
  electronic_device: '电子设备',
  fabric: '布料',
  metal: '金属',
  wood: '木质',
  glass: '玻璃',
  plastic: '塑料',
  leather: '皮革',
  stone: '石材',
  ceramic: '陶瓷',
  liquid: '液体',
  screen: '屏幕',
  black: '黑色',
  white: '白色',
  gray: '灰色',
  grey: '灰色',
  silver: '银色',
  gold: '金色',
  red: '红色',
  blue: '蓝色',
  green: '绿色',
  yellow: '黄色',
  brown: '棕色',
  transparent: '透明',
};

export function getStringValue(
  record: Record<string, unknown>,
  keys: string[]
): string | undefined {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return undefined;
}

function getBooleanValue(record: Record<string, unknown>, keys: string[]): boolean | undefined {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'boolean') return value;
  }
  return undefined;
}

const LOCATION_COLUMN_KEYS = ['timeOfDay', 'weather', 'season', 'atmosphere'] as const;

function getMetaData(record: Record<string, unknown>): Record<string, unknown> {
  const raw = record.metaData;
  return raw && typeof raw === 'object' && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : {};
}

function withLocationColumns(
  metaData: Record<string, unknown>,
  record: Record<string, unknown>
): Record<string, unknown> {
  const next = { ...metaData };
  for (const key of LOCATION_COLUMN_KEYS) {
    const value = getStringValue(record, [key]);
    if (value && !formatMetaValue(next[key])) next[key] = value;
  }
  return next;
}

function getNullableAssetText(record: Record<string, unknown>, key: string): string | undefined {
  const value = record[key];
  if (value === null) {
    const metaData = getMetaData(record);
    // 默认 null 仍可继承本体；同字段的元数据空值记录表示手动清空。
    if (metaData[key] === null || metaData[key] === '') return '';
    return getStringValue(metaData, [key]);
  }
  return typeof value === 'string' ? value : undefined;
}

function normalizeAssetVariants(value: unknown): AssetVariant[] {
  if (!Array.isArray(value)) return [];
  return value.reduce<AssetVariant[]>((variants, entry) => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return variants;
    const record = entry as Record<string, unknown>;
    const variantDraftId = getStringValue(record, ['variantDraftId']);
    const tagLabel = getStringValue(record, ['tagLabel', 'name']);
    if (!variantDraftId || !tagLabel) return variants;
    const metaData = withLocationColumns(getMetaData(record), record);
    const usage = getStringValue(record, ['usage']);
    if (usage && !formatMetaValue(metaData.usage)) {
      metaData.usage = usage;
    }
    variants.push({
      variantDraftId,
      tagLabel,
      isDefault: getBooleanValue(record, ['isDefault']),
      description: typeof record.description === 'string' ? record.description : undefined,
      visualDescription:
        typeof record.visualDescription === 'string' ? record.visualDescription : undefined,
      clothing: typeof record.clothing === 'string' ? record.clothing : undefined,
      hair: typeof record.hair === 'string' ? record.hair : undefined,
      makeup: typeof record.makeup === 'string' ? record.makeup : undefined,
      accessories: Array.isArray(record.accessories)
        ? record.accessories.filter((v): v is string => typeof v === 'string')
        : [],
      applicableScenes: Array.isArray(record.applicableScenes)
        ? record.applicableScenes.filter((v): v is string => typeof v === 'string')
        : [],
      timeOfDay: getNullableAssetText(record, 'timeOfDay'),
      weather: getNullableAssetText(record, 'weather'),
      season: getNullableAssetText(record, 'season'),
      atmosphere: typeof record.atmosphere === 'string' ? record.atmosphere : undefined,
      usage: typeof record.usage === 'string' ? record.usage : undefined,
      metaData,
    });
    return variants;
  }, []);
}

function normalizeAssetItems(value: unknown): AssetItem[] {
  if (!Array.isArray(value)) return [];
  return value.reduce<AssetItem[]>((items, item) => {
    if (!item || typeof item !== 'object') return items;
    const record = item as Record<string, unknown>;
    const name = typeof record.name === 'string' ? record.name.trim() : '';
    if (!name) return items;
    const description =
      typeof record.description === 'string' && record.description.trim()
        ? record.description.trim()
        : undefined;
    const metaData = withLocationColumns(getMetaData(record), record);
    const variants = normalizeAssetVariants(record.variants);
    const defaultVariantDraftId = getStringValue(record, ['defaultVariantDraftId']);
    items.push({
      schemaVersion: typeof record.schemaVersion === 'number' ? record.schemaVersion : 2,
      draftItemId: getStringValue(record, ['draftItemId']) ?? `asset_${items.length + 1}`,
      kind: getStringValue(record, ['kind']) ?? 'asset',
      name,
      description,
      metaData,
      roleType: getStringValue(record, ['roleType']),
      isOffscreen: getBooleanValue(record, ['isOffscreen']),
      gender: getStringValue(record, ['gender']) ?? getStringValue(metaData, ['gender']),
      race: getStringValue(record, ['race']) ?? getStringValue(metaData, ['race']),
      visualAge: getStringValue(record, ['visualAge']) ?? getStringValue(metaData, ['visualAge']),
      location: getNullableAssetText(record, 'location'),
      realWorldLocation:
        typeof record.realWorldLocation === 'string' ? record.realWorldLocation : undefined,
      propType: getNullableAssetText(record, 'propType'),
      ownerCharacter: typeof record.ownerCharacter === 'string' ? record.ownerCharacter : undefined,
      ownerLocation: typeof record.ownerLocation === 'string' ? record.ownerLocation : undefined,
      usage:
        getStringValue(record, ['usage']) ??
        getStringValue(metaData, ['usage']) ??
        usageFromDefaultVariant(variants, defaultVariantDraftId),
      defaultVariantDraftId,
      variants,
    });
    return items;
  }, []);
}

function usageFromDefaultVariant(
  variants: AssetVariant[],
  defaultVariantDraftId: string | undefined
): string | undefined {
  const variant =
    variants.find((item) => item.variantDraftId === defaultVariantDraftId) ??
    variants.find((item) => item.isDefault) ??
    variants[0];
  return variant ? (variant.usage ?? formatMetaValue(variant.metaData.usage)) : undefined;
}

function formatMetaValue(value: unknown): string | undefined {
  if (typeof value === 'string') return value.trim() || undefined;
  if (typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return value ? '是' : '否';
  if (Array.isArray(value)) {
    const values = value.map(formatMetaValue).filter(Boolean);
    return values.length ? values.join('、') : undefined;
  }
  return undefined;
}

function getMetaText(item: AssetItem, keys: string[]): string | undefined {
  for (const key of keys) {
    const value = formatMetaValue(item.metaData[key]);
    if (value) return value;
  }
  return undefined;
}

function getAssetMetadataText(item: AssetItem, keys: string[]): string | undefined {
  for (const key of keys) {
    const value = locationColumnText(item, key);
    if (value) return getAssetMetadataLabel(key, value);
  }
  return undefined;
}

function getDisplayText(value?: string | null): string | undefined {
  if (!value) return undefined;
  return ASSET_VALUE_LABELS[value] ?? value;
}

function getMappedMetaText(item: AssetItem, keys: string[]): string | undefined {
  const value = keys
    .map((key) => locationColumnText(item, key))
    .find((value) => value !== undefined);
  return getDisplayText(value);
}

function getOptionalEnumLabel(value: string | undefined): string | undefined {
  return value === '-' ? undefined : value;
}

export function getDefaultVariant(item: AssetItem): AssetVariant | undefined {
  return (
    item.variants.find((variant) => variant.variantDraftId === item.defaultVariantDraftId) ??
    item.variants.find((variant) => variant.isDefault) ??
    item.variants[0]
  );
}

export const ASSET_REVIEW_FIELDS = ['description', 'visualDescription', 'usage'] as const;
export type AssetReviewField = (typeof ASSET_REVIEW_FIELDS)[number];

export type AssetReviewNotice = {
  /** 能定位到卡片字段的意见，按字段去重。 */
  byField: Partial<Record<AssetReviewField, string[]>>;
  /** 无法定位到字段的意见（如原文依据不足）。 */
  general: string[];
  /** 去重后的意见总数。 */
  total: number;
};

/** 场次对用户不可见，适用场次相关意见不展示。 */
const HIDDEN_REVIEW_FIELDS = new Set(['applicableScenes']);

function isAssetReviewField(field: string | undefined): field is AssetReviewField {
  return (ASSET_REVIEW_FIELDS as readonly string[]).includes(field ?? '');
}

type ReviewIssue = { field?: string; message: string };

function readReviewIssues(metaData: Record<string, unknown> | undefined): ReviewIssue[] {
  const sourceReview = metaData?.sourceReview;
  const issues =
    sourceReview && typeof sourceReview === 'object'
      ? (sourceReview as { issues?: unknown }).issues
      : undefined;
  if (!Array.isArray(issues)) return [];
  return issues.flatMap((issue: unknown) => {
    if (!issue || typeof issue !== 'object') return [];
    const { field, message } = issue as Record<string, unknown>;
    if (typeof message !== 'string' || !message.trim()) return [];
    if (typeof field === 'string' && HIDDEN_REVIEW_FIELDS.has(field)) return [];
    return [{ message: message.trim(), field: typeof field === 'string' ? field : undefined }];
  });
}

/**
 * 资产卡片的待核对提示：不传变体时返回资产级意见；传变体时只返回该变体自己的意见。
 * 历史数据把资产级的泛泛意见复制到每个变体，这类无字段的同文意见交给基本信息卡统一展示；
 * 能定位到变体字段的意见即使与资产级同文也保留，否则该字段不会被标注。
 */
export function getAssetReviewNotice(
  item: AssetItem,
  variant?: AssetVariant
): AssetReviewNotice | null {
  const assetIssues = readReviewIssues(item.metaData);
  let issues = assetIssues;
  if (variant) {
    const assetMessages = new Set(assetIssues.map((issue) => issue.message));
    issues = readReviewIssues(variant.metaData).filter(
      (issue) => isAssetReviewField(issue.field) || !assetMessages.has(issue.message)
    );
  }
  if (!issues.length) return null;
  const byField: AssetReviewNotice['byField'] = {};
  const general: string[] = [];
  for (const { field, message } of issues) {
    const bucket = isAssetReviewField(field) ? (byField[field] ??= []) : general;
    if (!bucket.includes(message)) bucket.push(message);
  }
  return { byField, general, total: new Set(issues.map((issue) => issue.message)).size };
}

export function getCharacterOutfit(item: AssetItem) {
  const defaultVariant = getDefaultVariant(item);
  const outfitName = getDefaultTagName(item);
  const visualDescription =
    defaultVariant?.visualDescription ??
    getStringValue(defaultVariant?.metaData ?? {}, [
      'visualDescription',
      'appearance',
      'visualAnchor',
      'style',
      'clothing',
    ]) ??
    getMetaText(item, ['visualDescription', 'appearance', 'visualAnchor', 'style']);
  const voiceName =
    getStringValue(defaultVariant?.metaData ?? {}, ['voiceName', 'voiceModel']) ??
    getMetaText(item, ['voiceName', 'voiceModel']);
  const metadataSource = (keys: string[]): Pick<AssetInfoRow, 'field' | 'variantDraftId'> => {
    for (const source of [
      { metaData: defaultVariant?.metaData, variantDraftId: defaultVariant?.variantDraftId },
      { metaData: item.metaData, variantDraftId: null },
    ]) {
      const key = keys.find((key) => getStringValue(source.metaData ?? {}, [key]));
      if (key) return { field: `metaData.${key}`, variantDraftId: source.variantDraftId };
    }
    return {};
  };
  const rows = compactRows([
    {
      label: '性格描述',
      value: getMetaText(item, ['personality', 'persona']),
      ...assetRowSource(item, ['personality', 'persona'], 'meta'),
    },
    { label: '服饰', value: defaultVariant?.clothing, field: 'clothing' },
    { label: '发型', value: defaultVariant?.hair, field: 'hair' },
    { label: '妆容', value: defaultVariant?.makeup, field: 'makeup' },
    { label: '配饰', value: defaultVariant?.accessories?.join('、'), field: 'accessories' },
  ]).filter((row) => row.value !== '-');

  return {
    name: outfitName,
    visualDescription,
    visualSource:
      defaultVariant?.visualDescription !== undefined
        ? { field: 'visualDescription' as const, variantDraftId: defaultVariant.variantDraftId }
        : metadataSource(['visualDescription', 'appearance', 'visualAnchor', 'style', 'clothing']),
    voiceName: voiceName ?? '未配置音色',
    voiceSource: metadataSource(['voiceName', 'voiceModel']),
    rows,
    customFields: readCustomFields(defaultVariant?.metaData),
  };
}

export function getDefaultTagName(item: AssetItem): string {
  const defaultVariant = getDefaultVariant(item);
  if (defaultVariant?.tagLabel) return defaultVariant.tagLabel;
  return (
    getMetaText(item, [
      'defaultTagLabel',
      'tagLabel',
      'costume',
      'costumeName',
      'outfit',
      'outfitName',
      'set',
      'setName',
      'status',
      'statusName',
      'state',
      'stateName',
    ]) ?? '基础'
  );
}

function getRoleTypeLabel(value?: string): string | undefined {
  if (!value) return undefined;
  return ROLE_TYPE_LABELS[value] ?? value;
}

function normalizeEnumValue(
  value: string | undefined,
  labels: Record<string, string>,
  fallback: string
): string {
  const normalized = value?.trim();
  if (!normalized) return fallback;
  if (Object.prototype.hasOwnProperty.call(labels, normalized)) return normalized;
  const matched = Object.entries(labels).find(([, label]) => label === normalized);
  return matched?.[0] ?? fallback;
}

function normalizeRoleTypeValue(value: string | undefined): string {
  return normalizeEnumValue(value, ROLE_TYPE_LABELS, 'supporting');
}

function normalizeGenderValue(value: string | undefined): string {
  return normalizeEnumValue(value, GENDER_LABELS, 'unknown');
}

function normalizeRaceValue(value: string | undefined): string {
  return normalizeEnumValue(value, RACE_LABELS, 'human');
}

function normalizeVisualAgeValue(value: string | undefined): string {
  const normalized = value?.trim();
  if (!normalized) return 'unknown';
  const enumValue = normalizeEnumValue(normalized, VISUAL_AGE_LABELS, '');
  if (enumValue) return enumValue;

  const ageMatch = normalized.match(/(\d{1,3})\s*岁?/);
  if (ageMatch) {
    const age = Number(ageMatch[1]);
    if (age <= 2) return 'baby';
    if (age <= 12) return 'child';
    if (age <= 19) return 'teenager';
    if (age <= 35) return 'young_adult';
    if (age <= 59) return 'middle_aged';
    return 'elderly';
  }

  if (/婴儿|宝宝|幼儿/.test(normalized)) return 'baby';
  if (/儿童|小孩|小学生/.test(normalized)) return 'child';
  if (/少年|少女|青少年|十几岁|teen/i.test(normalized)) return 'teenager';
  if (/青年|年轻|二十|三十/.test(normalized)) return 'young_adult';
  if (/中年|四十|五十/.test(normalized)) return 'middle_aged';
  if (/老年|老人|六十|七十|八十/.test(normalized)) return 'elderly';

  return 'unknown';
}

export type AssetInfoRow = {
  label: string;
  value: string;
  field?: BreakdownReference['field'];
  /** null 表示父资产字段，避免继承装扮卡片的变体范围。 */
  variantDraftId?: string | null;
};

/** 与展示值采用同一来源优先级，引用不能把父级元数据误指向默认变体。 */
function assetRowSource(
  item: AssetItem,
  keys: string[],
  scope: 'root' | 'variant' | 'meta' = 'root'
): Pick<AssetInfoRow, 'field' | 'variantDraftId'> {
  const variant = scope === 'variant' ? getDefaultVariant(item) : undefined;
  for (const key of keys) {
    const sources = [
      ...(variant
        ? [
            {
              value: variant as unknown as Record<string, unknown>,
              prefix: '',
              id: variant.variantDraftId,
            },
            { value: variant.metaData, prefix: 'metaData.', id: variant.variantDraftId },
          ]
        : []),
      ...(scope === 'root'
        ? [{ value: item as unknown as Record<string, unknown>, prefix: '', id: null }]
        : []),
      { value: item.metaData, prefix: 'metaData.', id: null },
    ];
    for (const source of sources) {
      if (formatMetaValue(source.value[key]) !== undefined)
        return {
          field: `${source.prefix}${key}` as BreakdownReference['field'],
          variantDraftId: source.id,
        };
    }
  }
  return {};
}

export function compactRows(rows: Array<Omit<AssetInfoRow, 'value'> & { value?: string | null }>) {
  return rows.filter((row): row is AssetInfoRow => Boolean(row.value?.trim()));
}

export function getAssetBadges(
  _item: AssetItem,
  _group: AssetGroupKey
): Array<{ label: string; value: string }> {
  return [];
}

export function getAssetDetailRows(
  _item: AssetItem,
  _group: AssetGroupKey
): Array<{ label: string; value: string }> {
  return [];
}

export function getLocationBasicRows(item: AssetItem): AssetInfoRow[] {
  return compactRows([
    { label: '场景名称', value: item.name, field: 'name' },
    { label: '内外景', value: getLocationTypeLabel(item.location), field: 'location' },
    { label: '场景描述', value: item.description, field: 'description' },
    {
      label: '现实取景地参考',
      value: item.realWorldLocation ?? getMetaText(item, ['realWorldLocation']),
      ...assetRowSource(item, ['realWorldLocation']),
    },
  ]);
}

function locationColumnText(item: AssetItem, key: string): string | undefined {
  const variant = getDefaultVariant(item);
  if (key === 'timeOfDay' && variant?.timeOfDay !== undefined) return variant.timeOfDay;
  if (key === 'weather' && variant?.weather !== undefined) return variant.weather;
  if (key === 'season' && variant?.season !== undefined) return variant.season;
  if (key === 'atmosphere' && variant?.atmosphere !== undefined) return variant.atmosphere;
  if (key === 'usage' && variant?.usage !== undefined) return variant.usage;
  if (variant && Object.prototype.hasOwnProperty.call(variant.metaData, key)) {
    return typeof variant.metaData[key] === 'string'
      ? (variant.metaData[key] as string)
      : formatMetaValue(variant.metaData[key]);
  }
  return getMetaText(item, [key]);
}

function getLocationEnvironmentRows(item: AssetItem): AssetInfoRow[] {
  return compactRows([
    {
      label: '昼夜时间',
      value: getTimeOfDayLabel(locationColumnText(item, 'timeOfDay')),
      ...assetRowSource(item, ['timeOfDay'], 'variant'),
    },
    {
      label: '天气',
      value: getWeatherLabel(locationColumnText(item, 'weather')),
      ...assetRowSource(item, ['weather'], 'variant'),
    },
    {
      label: '季节',
      value: getSeasonLabel(locationColumnText(item, 'season')),
      ...assetRowSource(item, ['season'], 'variant'),
    },
    {
      label: '光线',
      value: locationColumnText(item, 'lighting') ?? locationColumnText(item, 'lightingDesign'),
      ...assetRowSource(item, ['lighting', 'lightingDesign'], 'variant'),
    },
    {
      label: '氛围',
      value: locationColumnText(item, 'atmosphere'),
      ...assetRowSource(item, ['atmosphere'], 'variant'),
    },
    {
      label: '色彩',
      value: locationColumnText(item, 'colorTone'),
      ...assetRowSource(item, ['colorTone'], 'variant'),
    },
    {
      label: '场景功能',
      value: getAssetMetadataText(item, ['sceneType', 'narrativePosition']),
      ...assetRowSource(item, ['sceneType', 'narrativePosition'], 'variant'),
    },
  ]).filter((row) => row.value !== '-');
}

export function getLocationMainSet(item: AssetItem) {
  return {
    name: getDefaultTagName(item),
    description: getDefaultVariant(item)?.visualDescription ?? getDefaultVariant(item)?.description,
    rows: getLocationEnvironmentRows(item),
    customFields: readCustomFields(getDefaultVariant(item)?.metaData),
  };
}

export function getPropBasicRows(item: AssetItem): AssetInfoRow[] {
  const resolvedPropType = item.propType ?? getMetaText(item, ['propType']);
  return compactRows([
    { label: '道具名称', value: item.name, field: 'name' },
    {
      label: '类型',
      value: getPropTypeLabel(resolvedPropType),
      ...assetRowSource(item, ['propType']),
    },
    { label: '道具视觉说明', value: item.description, field: 'description' },
  ]);
}

function getPropUsageRows(item: AssetItem): AssetInfoRow[] {
  const resolvedPropType = item.propType ?? getMetaText(item, ['propType']);
  const isSetDressing = resolvedPropType === 'set_dressing' || resolvedPropType === 'setDressing';
  return compactRows([
    {
      label: isSetDressing ? '道具布景地' : '道具使用者',
      ...assetRowSource(item, [isSetDressing ? 'ownerLocation' : 'ownerCharacter']),
      value: isSetDressing
        ? (item.ownerLocation ?? getMetaText(item, ['ownerLocation']))
        : (item.ownerCharacter ?? getMetaText(item, ['ownerCharacter'])),
    },
    {
      label: '使用方法/关键功能',
      value: locationColumnText(item, 'usage') ?? item.usage ?? '未填写',
      ...(locationColumnText(item, 'usage')
        ? assetRowSource(item, ['usage'], 'variant')
        : assetRowSource(item, ['usage'])),
    },
    {
      label: '材质',
      value: getMappedMetaText(item, ['material']),
      ...assetRowSource(item, ['material'], 'variant'),
    },
    {
      label: '尺寸',
      value: getMappedMetaText(item, ['size']),
      ...assetRowSource(item, ['size'], 'variant'),
    },
    {
      label: '颜色',
      value: getMappedMetaText(item, ['color']),
      ...assetRowSource(item, ['color'], 'variant'),
    },
    {
      label: '功能/状态',
      ...assetRowSource(
        item,
        ['productState', 'narrativeSignificance', 'visualImpact', 'effect'],
        'variant'
      ),
      value: getAssetMetadataText(item, [
        'productState',
        'narrativeSignificance',
        'visualImpact',
        'effect',
      ]),
    },
  ]);
}

export function getPropMainState(item: AssetItem) {
  return {
    name: getDefaultTagName(item),
    description: getDefaultVariant(item)?.visualDescription ?? getDefaultVariant(item)?.description,
    rows: getPropUsageRows(item),
    customFields: readCustomFields(getDefaultVariant(item)?.metaData),
  };
}

export function getCharacterBasicRows(item: AssetItem): AssetInfoRow[] {
  return compactRows([
    { label: '角色名称', value: item.name, field: 'name' },
    { label: '类型', value: getRoleTypeLabel(item.roleType), field: 'roleType' },
    { label: '性别', value: getOptionalEnumLabel(getGenderLabel(item.gender)), field: 'gender' },
    {
      label: '视觉年龄',
      value: getOptionalEnumLabel(getVisualAgeLabel(item.visualAge)),
      field: 'visualAge',
    },
    { label: '种族', value: getOptionalEnumLabel(getRaceLabel(item.race)), field: 'race' },
    {
      label: '是否为画外音角色',
      value: item.isOffscreen === undefined ? undefined : item.isOffscreen ? '是' : '否',
      field: 'isOffscreen',
    },
    { label: '角色简介', value: item.description, field: 'description' },
    {
      label: '记忆点',
      value: getMetaText(item, ['memorablePoint', 'emotionArc']),
      ...assetRowSource(item, ['memorablePoint', 'emotionArc'], 'meta'),
    },
    {
      label: '艺术风格',
      value: getMetaText(item, ['artStyle', 'makeupStyle']),
      ...assetRowSource(item, ['artStyle', 'makeupStyle'], 'meta'),
    },
  ]);
}

export function createCharacterBasicEditForm(item: AssetItem): CharacterBasicEditForm {
  return {
    originalName: item.name,
    name: item.name,
    roleType: normalizeRoleTypeValue(item.roleType),
    race: normalizeRaceValue(item.race),
    gender: normalizeGenderValue(item.gender),
    visualAge: normalizeVisualAgeValue(item.visualAge),
    isOffscreen: Boolean(item.isOffscreen),
    description: item.description ?? '',
  };
}

export function getAssetResult(session: CanonicalAISession | null): AssetResult | null {
  if (session?.sessionType !== SESSION_TYPE_SCREENPLAY_ASSET_BREAKDOWN) return null;
  const raw = session.payload?.assetResult;
  if (!raw || typeof raw !== 'object') return null;
  const source = raw as Record<string, unknown>;
  const result = {
    characters: normalizeAssetItems(source.characters),
    locations: normalizeAssetItems(source.locations),
    props: normalizeAssetItems(source.props),
  };
  return ASSET_GROUPS.some((group) => result[group.key].length > 0) ? result : null;
}

export function createEmptyAssetConfirmationState(): AssetConfirmationState {
  return {
    characters: { names: false, labels: false },
    locations: { names: false, labels: false },
    props: { names: false, labels: false },
  };
}

export type CustomFieldItem = { label: string; value: string };

export function readCustomFields(
  meta: Record<string, unknown> | null | undefined
): CustomFieldItem[] {
  if (!meta) return [];
  const raw = meta.customFields ?? meta.custom_fields;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const record = item as Record<string, unknown>;
    const label = typeof record.label === 'string' ? record.label.trim() : '';
    const value = typeof record.value === 'string' ? record.value.trim() : '';
    if (!label || !value) return [];
    return [{ label, value }];
  });
}

export function getAssetConfirmationLabels(item: AssetItem): string[] {
  const labels: string[] = [];
  if (item.roleType) labels.push(`类型:${getRoleTypeLabel(item.roleType) ?? item.roleType}`);
  if (item.isOffscreen !== undefined) labels.push(`画外音:${item.isOffscreen ? '是' : '否'}`);
  if (item.gender)
    labels.push(`性别:${getOptionalEnumLabel(getGenderLabel(item.gender)) ?? item.gender}`);
  if (item.visualAge) {
    labels.push(
      `视觉年龄:${getOptionalEnumLabel(getVisualAgeLabel(item.visualAge)) ?? item.visualAge}`
    );
  }
  if (item.race) labels.push(`种族:${getOptionalEnumLabel(getRaceLabel(item.race)) ?? item.race}`);
  if (item.location) labels.push(`内外景:${getLocationTypeLabel(item.location) ?? item.location}`);
  if (item.realWorldLocation) labels.push(`取景:${item.realWorldLocation}`);
  if (item.propType) labels.push(`道具类型:${getPropTypeLabel(item.propType) ?? item.propType}`);
  if (item.ownerCharacter) labels.push(`关联角色:${item.ownerCharacter}`);
  if (item.ownerLocation) labels.push(`关联场景:${item.ownerLocation}`);
  if (item.usage) labels.push(`用途:${item.usage}`);
  for (const [key, value] of Object.entries(item.metaData)) {
    if (key === 'customFields' || key === 'custom_fields') continue;
    const formatted = formatMetaValue(value);
    if (formatted) labels.push(`${key}:${formatted}`);
  }
  for (const variant of item.variants) {
    labels.push(`标签:${variant.tagLabel}`);
  }
  return labels;
}

export function getAssetDraftPanelCounts(
  assetResult: AssetResult,
  activeAssetGroup: AssetGroupKey,
  assetConfirmations: AssetConfirmationState
) {
  const totalAssetCount = ASSET_GROUPS.reduce(
    (count, group) => count + assetResult[group.key].length,
    0
  );
  const activeAssetItems = assetResult[activeAssetGroup];
  return {
    totalAssetCount,
    activeAssetCount: activeAssetItems.length,
    activeAssetLabelCount: activeAssetItems.reduce(
      (count, item) => count + getAssetConfirmationLabels(item).length,
      0
    ),
    confirmedAssetGroupCount: ASSET_GROUPS.filter((group) => {
      const confirmation = assetConfirmations[group.key];
      return confirmation.names && confirmation.labels;
    }).length,
    allAssetGroupsConfirmed: ASSET_GROUPS.every((group) => {
      const confirmation = assetConfirmations[group.key];
      return confirmation.names && confirmation.labels;
    }),
  };
}

export function getAssetConfirmationSignature(assetResult: AssetResult | null): string {
  if (!assetResult) return '';
  return JSON.stringify(
    ASSET_GROUPS.map((group) => ({
      group: group.key,
      items: assetResult[group.key].map((item) => ({
        name: item.name,
        labels: getAssetConfirmationLabels(item),
      })),
    }))
  );
}

export function getStoryboardItems(
  session: CanonicalAISession | null
): Array<Record<string, unknown>> {
  if (session?.sessionType !== SESSION_TYPE_SCREENPLAY_STORYBOARD_BREAKDOWN) return [];
  const raw = session.payload?.storyboardResult;
  if (!raw || typeof raw !== 'object') return [];
  const storyboards = (raw as Record<string, unknown>).storyboards;
  return Array.isArray(storyboards)
    ? storyboards.filter(
        (item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object'
      )
    : [];
}

export function getStoryboardTitle(item: Record<string, unknown>, index: number): string {
  return typeof item.title === 'string' && item.title.trim() ? item.title : `分镜 ${index + 1}`;
}

export function getStoryboardDescription(item: Record<string, unknown>): string {
  const description = item.description;
  if (typeof description === 'string' && description.trim()) return description;
  const shootingDescription = item.shootingDescription;
  return typeof shootingDescription === 'string' ? shootingDescription : '';
}

export const STORYBOARD_REVIEW_FIELDS = ['description', 'shootingDescription'] as const;
export type StoryboardReviewField = (typeof STORYBOARD_REVIEW_FIELDS)[number];

export type StoryboardReviewNotice = {
  byField: Partial<Record<StoryboardReviewField, string[]>>;
  general: string[];
  /** 复核模型摘录的原文约束依据，去重后整体展示。 */
  sourceQuotes: string[];
  total: number;
};

/**
 * 质量警告只有代码，说明文案与对应字段由前端固定映射；未列出的代码不展示。
 * 卡片不显示景别和运镜，`unrecognized_shot_size` / `unrecognized_camera_movement` 因此有意不列出。
 */
const STORYBOARD_REVIEW_RULES: Record<
  string,
  { message: string; fields: readonly StoryboardReviewField[] }
> = {
  multiple_edits_in_single_shot: {
    message:
      '拍摄描述包含镜头切换（如“切至”“再切”），一条分镜应只表达一个连续镜头。建议点击“拆分”，或改成连续运镜。',
    fields: ['shootingDescription'],
  },
  static_camera_movement_conflict: {
    message: '运镜为固定，但拍摄描述出现推、拉、摇、跟等运镜词，请核对镜头是否保持固定。',
    fields: ['shootingDescription'],
  },
  shot_size_conflict: {
    message: '景别与原文明确要求不一致，请对照原文依据修改。',
    fields: ['shootingDescription'],
  },
  description_shooting_overlap: {
    message: '内容描述与拍摄描述基本重复。内容描述写画面里发生什么，拍摄描述写怎么拍，请核对。',
    fields: ['description', 'shootingDescription'],
  },
  action_scope_conflict: {
    message: '包含当前场原文没有的动作，可能提前了后续场次的动作，请核对。',
    fields: ['description', 'shootingDescription'],
  },
  continuous_take_split: {
    message: '原文要求一镜到底的段落被拆成了多条分镜，请核对是否需要合并。',
    fields: [],
  },
  dialogue_source_order_mismatch: { message: '对白顺序与原文不同，请核对。', fields: [] },
  missing_visible_reference: {
    message: '画面中出镜的人物或物品缺少资产引用，请核对。',
    fields: [],
  },
  asset_state_requires_review: {
    message: '引用的资产状态与当前动作不匹配（如已脱下的衣服仍是穿着状态），请核对。',
    fields: [],
  },
  quality_review_incomplete: { message: '分镜质量核对未完成，请核对。', fields: [] },
};

export function readStoryboardSourceQuotes(metaData: Record<string, unknown>): string[] {
  const review = metaData.qualityReview;
  const issues =
    review && typeof review === 'object'
      ? (review as { sourceConstraintIssues?: unknown }).sourceConstraintIssues
      : undefined;
  if (!Array.isArray(issues)) return [];
  const quotes = issues.flatMap((issue: unknown) => {
    const quote =
      issue && typeof issue === 'object' ? (issue as { quote?: unknown }).quote : undefined;
    return typeof quote === 'string' && quote.trim() ? [quote.trim()] : [];
  });
  return [...new Set(quotes)];
}

export function getStoryboardReviewNotice(
  item: Record<string, unknown>
): StoryboardReviewNotice | null {
  const metaData = item.metaData;
  if (!metaData || typeof metaData !== 'object') return null;
  const meta = metaData as Record<string, unknown>;
  if (!Array.isArray(meta.qualityWarnings)) return null;
  const rules = [...new Set(meta.qualityWarnings)].flatMap((code) =>
    typeof code === 'string' && STORYBOARD_REVIEW_RULES[code] ? [STORYBOARD_REVIEW_RULES[code]] : []
  );
  if (!rules.length) return null;
  const byField: StoryboardReviewNotice['byField'] = {};
  const general: string[] = [];
  for (const { message, fields } of rules) {
    if (!fields.length) general.push(message);
    for (const field of fields) (byField[field] ??= []).push(message);
  }
  return { byField, general, sourceQuotes: readStoryboardSourceQuotes(meta), total: rules.length };
}

export function getStoryboardString(
  item: Record<string, unknown>,
  keys: string[]
): string | undefined {
  for (const key of keys) {
    const value = item[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return undefined;
}

export function getStoryboardNumber(
  item: Record<string, unknown>,
  keys: string[]
): number | undefined {
  for (const key of keys) {
    const value = item[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim() && Number.isFinite(Number(value))) {
      return Number(value);
    }
  }
  return undefined;
}

function getStoryboardStringList(item: Record<string, unknown>, keys: string[]): string[] {
  for (const key of keys) {
    const value = item[key];
    if (!Array.isArray(value)) continue;
    return value
      .map((entry) => {
        if (typeof entry === 'string') return entry.trim();
        if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return '';
        const record = entry as Record<string, unknown>;
        const name = getStringValue(record, ['name']);
        const tagLabel = getStringValue(record, ['tagLabel']);
        if (name && tagLabel) return `${name} · ${tagLabel}`;
        return name ?? tagLabel ?? '';
      })
      .filter((entry) => entry.length > 0);
  }
  return [];
}

export function getStoryboardDialogueTypeLabel(value: number | undefined): string | undefined {
  if (value === 1) return '对白';
  if (value === 2) return '内心独白';
  if (value === 3) return '旁白';
  return undefined;
}

export function getStoryboardDialogues(item: Record<string, unknown>) {
  const dialogues = item.dialogues;
  if (!Array.isArray(dialogues)) return [];
  return dialogues
    .filter(
      (dialogue): dialogue is Record<string, unknown> =>
        Boolean(dialogue) && typeof dialogue === 'object'
    )
    .map((dialogue) => ({
      content: getStoryboardString(dialogue, ['content', 'text']),
      characterName: getStoryboardString(dialogue, ['characterName']),
      emotion: getStoryboardString(dialogue, ['emotion']),
      dialogueType: getStoryboardNumber(dialogue, ['dialogueType']),
    }))
    .filter((dialogue) => dialogue.content);
}

export function getStoryboardFieldRows(item: Record<string, unknown>) {
  const sceneNumber = getStoryboardString(item, ['sceneNumber']);
  const sceneTitle = getStoryboardString(item, ['sceneTitle']);
  const shotSize = getStoryboardString(item, ['shotSize']);
  const cameraMovement = getStoryboardString(item, ['cameraMovement']);
  const duration = getStoryboardNumber(item, ['estimatedDuration']);
  return compactRows([
    {
      label: '场次',
      value:
        sceneNumber || sceneTitle
          ? [sceneNumber, sceneTitle].filter(Boolean).join(' · ')
          : undefined,
    },
    {
      label: '景别',
      value: shotSize ? getShotSizeLabel(shotSize) : undefined,
    },
    {
      label: '运镜',
      value: cameraMovement ? getCameraMovementLabel(cameraMovement) : undefined,
    },
    { label: '预估时长', value: duration ? `${duration}s` : undefined },
  ]);
}

export function getStoryboardResources(item: Record<string, unknown>) {
  return {
    characters: getStoryboardStringList(item, ['characters']),
    locations: getStoryboardStringList(item, ['locations']),
    props: getStoryboardStringList(item, ['props']),
  };
}

/** 人工进度独立于系统质量意见，不参与最终写入门禁。 */
export function isStoryboardManuallyReviewed(item: { metaData?: unknown }): boolean {
  const metadata = item.metaData;
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) return false;
  const marker = (metadata as Record<string, unknown>).manualReview;
  return Boolean(
    marker &&
    typeof marker === 'object' &&
    !Array.isArray(marker) &&
    (marker as Record<string, unknown>).reviewed === true
  );
}

/** 资产基础信息、变体和分镜使用同一人工核对元数据。 */
export const isDraftManuallyReviewed = isStoryboardManuallyReviewed;

/** 人工核对统计复用已有基本信息与变体的核对范围。 */
export function getAssetReviewProgress(result: AssetResult | null) {
  const targets = Object.values(result ?? {}).flatMap((items) =>
    items.flatMap((item: AssetItem) =>
      [item, ...item.variants].filter(
        (target) =>
          isDraftManuallyReviewed(target) ||
          Boolean(getAssetReviewNotice(item, 'variantDraftId' in target ? target : undefined))
      )
    )
  );
  return { reviewed: targets.filter(isDraftManuallyReviewed).length, total: targets.length };
}
